import React, { useEffect, useState } from 'react';
import { db } from '../services/firebase';
import { collection, getDocs, query, orderBy, doc, deleteDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit, Trash2, ArrowLeft } from 'lucide-react';

interface BlogPost {
  id: string;
  title: string;
  excerpt: string;
  content: string;
  author: string;
  date: string;
  category: string;
  tags: string[];
  image?: string;
}

const AdminBlogPage: React.FC = () => {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [fetchingPosts, setFetchingPosts] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    setFetchingPosts(true);
    try {
      const q = query(collection(db, 'blogs'), orderBy('date', 'desc'));
      const snapshot = await getDocs(q);
      const data = snapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() })) as BlogPost[];
      setPosts(data);
    } catch (err) {
      setError('Failed to fetch blog posts.');
    } finally {
      setFetchingPosts(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this blog post?')) return;
    try {
      await deleteDoc(doc(db, 'blogs', id));
      setPosts(posts => posts.filter(p => p.id !== id));
    } catch (err) {
      setError('Failed to delete blog post.');
    }
  };

  return (
    <div className="mx-auto max-w-3xl md:border-x">
      <div className="px-4 py-12">
        <button
          onClick={() => navigate('/admin')}
          className="mb-6 text-sm text-muted-foreground hover:text-foreground flex items-center gap-2 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Dashboard
        </button>

        <div className="flex items-center justify-between mb-8">
          <h1 className="text-2xl font-bold text-foreground">Articles</h1>
          <button
            onClick={() => navigate('/admin/articles/new')}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium border border-border rounded-md hover:bg-accent transition-colors"
          >
            <Plus size={14} />
            New Article
          </button>
        </div>

        {error && <p className="text-red-500 text-sm mb-4">{error}</p>}

        {fetchingPosts ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-foreground" />
          </div>
        ) : posts.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-12">
            No articles yet. Create your first one!
          </p>
        ) : (
          <div className="space-y-1">
            {posts.map(post => (
              <div
                key={post.id}
                className="flex items-center justify-between py-4 screen-line-after"
              >
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-foreground">{post.title}</h3>
                  <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                    <span>{new Date(post.date).toLocaleDateString()}</span>
                    <span>·</span>
                    <span>{post.category}</span>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => navigate(`/admin/articles/edit/${post.id}`)}
                    className="p-2 text-muted-foreground hover:text-foreground transition-colors"
                    title="Edit"
                  >
                    <Edit size={14} />
                  </button>
                  <button
                    onClick={() => handleDelete(post.id)}
                    className="p-2 text-muted-foreground hover:text-red-500 transition-colors"
                    title="Delete"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminBlogPage;